import { resolveTiPremiumPrice } from '../../calculatedAttributes/providers/tiQuote';
import type { CmsProduct } from '../../types/cmsProduct';
import type { TravelPremiumsData } from '../../types/travelInsurance';
import { compareIdOf } from './compareUrl';
import { COMPARE_VARIANT_LAYOUT, type CompareVariant } from './compareVariant';

/**
 * A plan "+ Add Plan" may offer, as small as the list needs: what names and pictures it, and the
 * one attribute that tells whether the trip prices it. The comparison page loads these from the
 * catalogue, and a full product only for the plan the reader picks. Every field is one `CmsProduct`
 * has, so a candidate reads through the same helpers a product does (`compareIdOf`,
 * `compareImageOf`, `resolveTiPremiumPrice`).
 */
export type CompareCandidate = Pick<
  CmsProduct,
  | 'uid'
  | 'entryUID'
  | 'title'
  | 'product_name'
  | 'product_image'
  | 'provider'
  | 'attributes'
>;

export type CompareOfferableInput = {
  variant: CompareVariant;
  /** What the catalogue holds for this comparison, in its order. */
  candidates: CompareCandidate[];
  /** The trip's premiums once the quote has settled; absent while it runs or where nothing prices. */
  premiums?: TravelPremiumsData;
  /** Plans removed on this page: they can always come back, priced or not, listed or not. */
  removed: CompareCandidate[];
  /** Plans already compared, never offered again. */
  selected: readonly string[];
};

/**
 * What "+ Add Plan" lists. A vertical that prices a trip offers what its results page shows for that
 * trip: the plans it priced. Until the quote has settled, or if it never does, the list stands as it
 * is rather than empty.
 */
export function compareOfferable({
  variant,
  candidates,
  premiums,
  removed,
  selected,
}: CompareOfferableInput): CompareCandidate[] {
  const priced =
    COMPARE_VARIANT_LAYOUT[variant].pricesTrip && premiums
      ? candidates.filter(
          (candidate) =>
            resolveTiPremiumPrice(candidate, premiums) !== undefined,
        )
      : candidates;

  const result: CompareCandidate[] = [];
  const seen = new Set<string>();
  for (const candidate of [...priced, ...removed]) {
    const id = compareIdOf(candidate);
    if (!id || seen.has(id) || selected.includes(id)) continue;
    seen.add(id);
    result.push(candidate);
  }
  return result;
}
